'use client';

import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { type Editor, EditorContent, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { common, createLowlight } from 'lowlight';
import {
  Bold,
  Code,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  MoreVertical,
  Paperclip,
  Quote,
  Send,
  Strikethrough,
  Terminal,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import type { WebsocketProvider } from 'y-websocket';
import type * as Y from 'yjs';
import { cn } from '../../lib/utils';
import type { EditorAttachment, User } from '../../types/comments';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Separator } from '../../ui/separator';
import { AttachmentList } from '../attachment-list';
import { MentionList, type MentionListHandle } from './MentionList';
import { animateMentionPopupIn } from './mention-popup-motion';
import {
  handleTiptapEditorKeyDown,
  isMentionSuggestionActive,
  mentionSuggestionPluginKey,
} from './tiptap-editor-keydown';

const lowlight = createLowlight(common);

export interface TiptapEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  onSubmit?: (content: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  users?: User[];
  minRows?: number;
  maxRows?: number;
  mode?: 'message' | 'note' | 'draft';
  submitLabel?: string;
  className?: string;
  attachments?: EditorAttachment[];
  onRemoveAttachment?: (index: number) => void;
  onPaperclipClick?: () => void;
  /** When provided (with `provider`), enables live multi-user collaborative editing via Yjs. */
  ydoc?: Y.Doc;
  provider?: WebsocketProvider;
  collabUser?: { name: string; color: string };
  /** Fired once the underlying Tiptap Editor instance is created. */
  onEditorReady?: (editor: Editor) => void;
}

const ToolbarButton = ({
  isActive,
  onClick,
  children,
  title,
}: {
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title: string;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="icon-sm"
    onClick={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={cn(isActive && 'bg-muted')}
    title={title}
  >
    {children}
    <span className="sr-only">{title}</span>
  </Button>
);

export const TiptapEditor = React.forwardRef<HTMLDivElement, TiptapEditorProps>(
  (
    {
      value = '',
      onChange,
      onSubmit,
      placeholder = 'Write something...',
      autoFocus = false,
      users = [],
      minRows = 3,
      maxRows = 10,
      mode = 'note',
      submitLabel = 'Send',
      className,
      attachments = [],
      onRemoveAttachment,
      onPaperclipClick,
      ydoc,
      provider,
      collabUser,
      onEditorReady,
    },
    ref
  ) => {
    const isCollaborative = !!ydoc;
    const usersRef = useRef(users);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
      if (!containerRef.current) return;

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setWidth(entry.contentRect.width);
        }
      });

      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, []);

    const isNarrow = width > 0 && width < 400;

    useEffect(() => {
      usersRef.current = users;
    }, [users]);

    const extensions = useMemo(
      () => [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          codeBlock: false,
          link: false,
          // Yjs/Collaboration manages undo history instead when collaborative.
          undoRedo: isCollaborative ? false : undefined,
        }),
        ...(isCollaborative && ydoc ? [Collaboration.configure({ document: ydoc })] : []),
        ...(isCollaborative && provider && collabUser
          ? [CollaborationCaret.configure({ provider, user: collabUser })]
          : []),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-primary underline',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
        CodeBlockLowlight.configure({
          lowlight,
        }),
        // react-hooks/refs misfires on the ReactRenderer<MentionListHandle> usage inside the
        // suggestion callbacks below — it's tiptap's own documented @mention render pattern
        // (creating a ReactRenderer and reading its imperative handle), not a React ref read
        // during this component's render.
        /* eslint-disable react-hooks/refs */
        Mention.configure({
          HTMLAttributes: {
            class: 'mention text-primary font-semibold',
          },
          suggestion: {
            pluginKey: mentionSuggestionPluginKey,
            items: ({ query }: { query: string }) => {
              return (usersRef.current || [])
                .filter(
                  (u) =>
                    (u.fullName || '').toLowerCase().includes(query.toLowerCase()) ||
                    (u.email || '').toLowerCase().includes(query.toLowerCase())
                )
                .slice(0, 5);
            },
            render: () => {
              let component: ReactRenderer<MentionListHandle>;
              let popup: TippyInstance[];

              // react/prop-types misfires here: these are tiptap suggestion callbacks
              // (typed via SuggestionProps/SuggestionKeyDownProps above), not React components.
              /* eslint-disable react/prop-types */
              return {
                onStart: (props: SuggestionProps) => {
                  component = new ReactRenderer(MentionList, {
                    props,
                    editor: props.editor,
                  });

                  if (!props.clientRect) return;

                  popup = tippy('body', {
                    getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(0, 0, 0, 0),
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    onMount: animateMentionPopupIn,
                  });
                },

                onUpdate(props: SuggestionProps) {
                  component.updateProps(props);
                  if (!props.clientRect) return;
                  if (popup?.[0]) {
                    popup[0].setProps({
                      getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(0, 0, 0, 0),
                    });
                  }
                },

                onKeyDown(props: SuggestionKeyDownProps) {
                  if (props.event.key === 'Escape') {
                    if (popup?.[0]) popup[0].hide();
                    return true;
                  }
                  return component.ref?.onKeyDown?.(props) ?? false;
                },

                onExit() {
                  if (popup?.[0]) popup[0].destroy();
                  if (component) component.destroy();
                },
              };
              /* eslint-enable react/prop-types */
            },
          },
        }),
        /* eslint-enable react-hooks/refs */
      ],
      [placeholder, isCollaborative, ydoc, provider, collabUser]
    );

    const editor: Editor | null = useEditor({
      extensions,
      // When collaborative, the Y.Doc (synced via Collaboration) is the sole
      // content source; setting `content` too would conflict with it.
      content: isCollaborative ? undefined : value,
      editorProps: {
        attributes: {
          class: cn(
            'prose prose-sm dark:prose-invert max-w-none focus:outline-none px-3 py-2 text-foreground text-sm',
            'prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1'
          ),
          style: `min-height: ${minRows * 1.5}rem; max-height: ${maxRows * 1.5}rem; overflow-y: auto;`,
        },
        handleKeyDown: (view, event): boolean => {
          return handleTiptapEditorKeyDown({
            event,
            mode,
            hasContent: view.state.doc.textContent.trim() !== '',
            mentionSuggestionActive: isMentionSuggestionActive(view.state),
            submit: onSubmit ? handleContentSubmit : undefined,
          });
        },
      },
      onUpdate: ({ editor }) => {
        setIsEditorEmpty(editor.getText().trim().length === 0);
        if (onChange) {
          onChange(editor.getHTML());
        }
      },
      onCreate: ({ editor }) => {
        setIsEditorEmpty(editor.getText().trim().length === 0);
        if (onEditorReady) {
          onEditorReady(editor);
        }
      },
    });

    useEffect(() => {
      if (editor && autoFocus) {
        editor.commands.focus('end');
      }
    }, [editor, autoFocus]);

    useEffect(() => {
      if (!isCollaborative && editor && value !== editor.getHTML()) {
        editor.commands.setContent(value, { emitUpdate: false });
      }
    }, [value, editor, isCollaborative]);

    const setLink = useCallback(() => {
      if (!editor) return;
      const previousUrl = editor.getAttributes('link').href;
      const url = window.prompt('URL', previousUrl);
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    const handleContentSubmit: () => void = useCallback(() => {
      if (!editor || !onSubmit) return;

      const html = editor.getHTML();
      const text = editor.getText().trim();

      // Ensure we have meaningful text to submit
      if (text.length === 0) return;

      onSubmit(html);
      editor.commands.clearContent();
    }, [editor, onSubmit]);

    if (!editor) return null;

    return (
      <div className={cn('flex flex-col gap-2', className)} ref={containerRef}>
        <div
          ref={ref}
          className="flex flex-col border border-border rounded-md bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/50 transition-shadow"
        >
          <div className="flex-1 cursor-text bg-background" onClick={() => editor.commands.focus()}>
            <EditorContent editor={editor} />
          </div>

          <AttachmentList attachments={attachments} onRemove={onRemoveAttachment} />

          <div className="flex items-center justify-between border-t border-border bg-muted/5 px-2 py-1.5">
            <div className="flex items-center gap-0.5">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive('bold')}
                title="Bold"
              >
                <Bold className="size-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive('italic')}
                title="Italic"
              >
                <Italic className="size-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                isActive={editor.isActive('strike')}
                title="Strikethrough"
              >
                <Strikethrough className="size-4" />
              </ToolbarButton>
              <ToolbarButton onClick={setLink} isActive={editor.isActive('link')} title="Link">
                <LinkIcon className="size-4" />
              </ToolbarButton>
              {onPaperclipClick && (
                <ToolbarButton onClick={onPaperclipClick} isActive={false} title="Attach file">
                  <Paperclip className="size-4" />
                </ToolbarButton>
              )}

              {!isNarrow ? (
                <>
                  <Separator orientation="vertical" className="mx-1 h-4" />

                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    isActive={editor.isActive('code')}
                    title="Inline Code"
                  >
                    <Code className="size-4" />
                  </ToolbarButton>

                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    title="Bullet List"
                  >
                    <List className="size-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    title="Numbered List"
                  >
                    <ListOrdered className="size-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive('blockquote')}
                    title="Blockquote"
                  >
                    <Quote className="size-4" />
                  </ToolbarButton>
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    isActive={editor.isActive('codeBlock')}
                    title="Code Block"
                  >
                    <Terminal className="size-4" />
                  </ToolbarButton>
                </>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon-sm" className="h-8 w-8 p-0">
                      <MoreVertical className="size-4" />
                      <span className="sr-only">More formatting</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleCode().run()}
                      className={cn(editor.isActive('code') && 'bg-muted')}
                    >
                      <Code className="mr-2 h-4 w-4" />
                      <span>Inline Code</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      className={cn(editor.isActive('bulletList') && 'bg-muted')}
                    >
                      <List className="mr-2 h-4 w-4" />
                      <span>Bullet List</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      className={cn(editor.isActive('orderedList') && 'bg-muted')}
                    >
                      <ListOrdered className="mr-2 h-4 w-4" />
                      <span>Numbered List</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleBlockquote().run()}
                      className={cn(editor.isActive('blockquote') && 'bg-muted')}
                    >
                      <Quote className="mr-2 h-4 w-4" />
                      <span>Blockquote</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                      className={cn(editor.isActive('codeBlock') && 'bg-muted')}
                    >
                      <Terminal className="mr-2 h-4 w-4" />
                      <span>Code Block</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {onSubmit && (
              <Button
                type="button"
                size="sm"
                variant="default"
                className="h-8 gap-1.5 px-3"
                onClick={handleContentSubmit}
                disabled={isEditorEmpty}
              >
                <Send className="size-3.5" />
                <span className="text-xs font-semibold">{submitLabel}</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
);

TiptapEditor.displayName = 'TiptapEditor';
